import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money, sized } from "@/lib/api";
import { setScrollLock } from "@/lib/lenis";
import { EASE } from "@/components/Reveal";

const CartLine = ({ line, onQty, onRemove, busy }) => {
  const m = line.merchandise;
  const image = m?.image || m?.product?.images?.nodes?.[0];
  return (
    <li data-testid="cart-line" className="flex gap-4 border-b py-5">
      <Link to={`/products/${m?.product?.handle}`} className="block w-24 shrink-0 bg-[#E8E5DF]">
        {image && <img src={sized(image.url, 300)} alt={image.altText || m?.product?.title} className="aspect-[3/4] w-full object-cover" loading="lazy" />}
      </Link>
      <div className="flex flex-1 flex-col">
        <div className="flex justify-between gap-3">
          <p className="font-display text-sm font-medium uppercase leading-snug">{m?.product?.title}</p>
          <p className="label shrink-0">{money(line.cost?.totalAmount)}</p>
        </div>
        {m?.title !== "Default Title" && <p className="label mt-1 text-[10px] text-[#111111]/60">{m?.title}</p>}
        <div className="mt-auto flex items-center justify-between pt-4">
          <div className="flex items-center border">
            <button data-testid="cart-line-decrease" aria-label="Decrease quantity" disabled={busy} onClick={() => onQty(line.quantity - 1)} className="grid h-10 w-10 place-items-center hover:text-[#E2541B] disabled:opacity-40"><Minus size={14} /></button>
            <span data-testid="cart-line-qty" className="label w-8 text-center">{line.quantity}</span>
            <button data-testid="cart-line-increase" aria-label="Increase quantity" disabled={busy} onClick={() => onQty(line.quantity + 1)} className="grid h-10 w-10 place-items-center hover:text-[#E2541B] disabled:opacity-40"><Plus size={14} /></button>
          </div>
          <button data-testid="cart-line-remove" disabled={busy} onClick={onRemove} className="label draw-link text-[10px] text-[#111111]/70">Remove</button>
        </div>
      </div>
    </li>
  );
};

export const CartDrawer = () => {
  const { cart, open, setOpen, update, remove, busy } = useCart();
  const lines = Array.isArray(cart?.lines?.nodes) ? cart.lines.nodes : [];

  useEffect(() => {
    setScrollLock(open);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60]">
          <motion.div data-testid="cart-overlay" className="absolute inset-0 bg-[#111111]/30 backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
          <motion.aside
            data-testid="cart-drawer"
            role="dialog"
            aria-label="Cart"
            data-lenis-prevent
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="absolute right-0 top-0 flex h-full w-full max-w-[440px] flex-col bg-[#F4F2EE]"
          >
            <div className="flex h-16 items-center justify-between border-b px-5 md:h-[72px] md:px-6">
              <p className="label">Cart ({cart?.totalQuantity ?? 0})</p>
              <button data-testid="cart-close-button" aria-label="Close cart" onClick={() => setOpen(false)} className="-mr-2 grid h-11 w-11 place-items-center hover:text-[#E2541B]"><X size={20} strokeWidth={1.5} /></button>
            </div>
            {lines.length === 0 ? (
              <div data-testid="cart-empty" className="flex flex-1 flex-col items-start justify-center px-6">
                <p className="font-display text-4xl font-bold tracking-[-0.05em]">Nothing here yet.</p>
                <Link to="/shop" onClick={() => setOpen(false)} className="label draw-link mt-6">Browse the shop</Link>
              </div>
            ) : (
              <>
                <ul className="flex-1 overflow-y-auto px-5 md:px-6">
                  {lines.map((l) => (
                    <CartLine key={l.id} line={l} busy={busy} onQty={(q) => (q < 1 ? remove(l.id) : update(l.id, q))} onRemove={() => remove(l.id)} />
                  ))}
                </ul>
                <div className="border-t px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-5 md:px-6">
                  <div className="flex items-baseline justify-between">
                    <p className="label">Subtotal</p>
                    <p data-testid="cart-subtotal" className="font-display text-xl font-semibold">{money(cart?.cost?.subtotalAmount)}</p>
                  </div>
                  <p className="mt-1 text-xs text-[#111111]/65">Shipping and taxes calculated at checkout.</p>
                  <a data-testid="cart-checkout-button" href={cart?.checkoutUrl} className="label mt-5 flex h-14 w-full items-center justify-center bg-[#111111] text-[#F4F2EE] transition-colors duration-300 hover:bg-[#E2541B]">
                    Checkout
                  </a>
                </div>
              </>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};
